// @flow
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Heading, Stack } from '@locus-taxy/neo-ui';

import { rankWith, uiTypeIs } from '@jsonforms/core';
import { JsonFormsDispatch, useJsonForms } from '@jsonforms/react';

import useWatch from 'common/hooks/useWatch';

import useLayoutProps from '../util/useLayoutProps';
import { getControlProps } from '../util/useControlProps';

type Props = {
    uischema: $FixMe,
    schema: $FixMe,
    path: string
};

const useGetControlProps = ({ uischema, schema, path }: Props) => {
    const ctx = useJsonForms();

    const [parent, child] = uischema.elements;
    // Child controls are wrapped in VerticalLayout. So extracting elements here.
    const elements = [parent, ...child.elements];

    return elements.map(element =>
        getControlProps(ctx, {
            uischema: element,
            schema,
            path
        })
    );
};

const BooleanGroupLayout = (props: $FixMe): React.Node => {
    const { t } = useTranslation('platform');

    const {
        uischema,
        schema,
        path,
        renderers,
        cells,
        enabled
    } = useLayoutProps(props);

    const controlProps = useGetControlProps({
        uischema,
        schema,
        path
    });

    const [parent, ...children] = controlProps;
    const { handleChange } = parent;

    useWatch(parent.data, () => {
        children.forEach(({ path, data }) => {
            if (parent.data !== data) {
                handleChange(path, parent.data);
            }
        });
    });

    const areAllChildrenFalse = children.every(({ data }) => !data);

    useWatch(areAllChildrenFalse, () => {
        // all children's are false - set parent to false
        if (areAllChildrenFalse && parent.data) {
            handleChange(parent.path, false);
        }
    });

    return (
        <Stack space="medium">
            <Heading size="large" color="grey.900">
                {t(uischema.label)}
            </Heading>

            {uischema.elements.map((element, index) => (
                <JsonFormsDispatch
                    key={index}
                    uischema={element}
                    schema={schema}
                    path={path}
                    enabled={enabled}
                    renderers={renderers}
                    cells={cells}
                />
            ))}
        </Stack>
    );
};

export default BooleanGroupLayout;

export const booleanGroupLayoutTester = rankWith(
    1,
    uiTypeIs('BooleanGroupLayout')
);
